import React, { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

/** How far before the list end the next page starts loading. */
const PRELOAD_MARGIN = '0px 0px 400px 0px';

export interface LoadMoreSentinelProps {
  /** The scroll container (header search panel). */
  rootRef: RefObject<HTMLElement>;
  /**
   * Re-arms the observer when it changes (new rows, a fetch settling), so a
   * still-visible end loads on — a reach skipped during a fetch is not lost.
   */
  armKey: string;
  onReach: () => void;
}

/** Invisible marker after a list: calls `onReach` when scrolled near it. */
export const LoadMoreSentinel: React.FC<LoadMoreSentinelProps> = ({ rootRef, armKey, onReach }) => {
  const ref = useRef<HTMLDivElement>(null);
  const onReachRef = useRef(onReach);
  onReachRef.current = onReach;

  useEffect(() => {
    const target = ref.current;
    const root = rootRef.current;
    if (!target || !root) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onReachRef.current();
    }, { root, rootMargin: PRELOAD_MARGIN });
    observer.observe(target);
    return () => observer.disconnect();
  }, [rootRef, armKey]);

  return <div ref={ref} aria-hidden="true" />;
};
